import { useState } from 'react';
import { Card, Row, Col, Statistic, Table, DatePicker, Space, Tag } from 'antd';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { apiClient } from '../api/client';
import { formatCurrency } from '../utils/formatters';

const { RangePicker } = DatePicker;

interface MetricsSummary {
  start_date: string;
  end_date: string;
  total_visits: number;
  total_orders: number;
  total_payments: number;
  total_completed: number;
  total_revenue: number;
  conversion_rate: number;
  avg_order_value: number;
}

interface ChannelPerformance {
  channel: string;
  visits: number;
  form_starts: number;
  orders_created: number;
  orders_paid: number;
  orders_completed: number;
  revenue: number;
  conversion_rate_order: number;
  conversion_rate_payment: number;
}

interface UTMPerformance {
  utm_source: string;
  utm_medium: string | null;
  utm_campaign: string | null;
  visits: number;
  orders_created: number;
  orders_paid: number;
  revenue: number;
  cost: number | null;
  roas: number | null;
  conversion_rate: number;
}

const fetchSummary = async (startDate: string, endDate: string): Promise<MetricsSummary> => {
  const resp = await apiClient.get<MetricsSummary>('/analytics/summary', {
    params: { start_date: startDate, end_date: endDate },
  });
  return resp.data;
};

const fetchChannelPerf = async (startDate: string, endDate: string): Promise<ChannelPerformance[]> => {
  const resp = await apiClient.get<ChannelPerformance[]>('/analytics/channel-performance', {
    params: { start_date: startDate, end_date: endDate },
  });
  return resp.data;
};

const fetchUTMPerf = async (startDate: string, endDate: string): Promise<UTMPerformance[]> => {
  const resp = await apiClient.get<UTMPerformance[]>('/analytics/utm-performance', {
    params: { start_date: startDate, end_date: endDate },
  });
  return resp.data;
};

export const Analytics: React.FC = () => {
  const [dateRange, setDateRange] = useState<[string, string]>([
    dayjs().subtract(30, 'day').format('YYYY-MM-DD'),
    dayjs().format('YYYY-MM-DD'),
  ]);
  const [start, end] = dateRange;

  const { data: summary, isLoading: loadingSummary, isError: errorSummary } = useQuery({
    queryKey: ['analytics-summary', start, end],
    queryFn: () => fetchSummary(start, end),
    staleTime: 60000,
  });

  const { data: channelPerf, isLoading: loadingChannel, isError: errorChannel } = useQuery({
    queryKey: ['analytics-channel', start, end],
    queryFn: () => fetchChannelPerf(start, end),
    staleTime: 60000,
  });

  const { data: utmPerf, isLoading: loadingUTM, isError: errorUTM } = useQuery({
    queryKey: ['analytics-utm', start, end],
    queryFn: () => fetchUTMPerf(start, end),
    staleTime: 60000,
  });

  const channelColumns = [
    { title: 'Канал', dataIndex: 'channel', key: 'channel', render: (v: string) => <Tag>{v}</Tag> },
    { title: 'Візити', dataIndex: 'visits', key: 'visits', align: 'right' as const },
    { title: 'Замовлень', dataIndex: 'orders_created', key: 'orders_created', align: 'right' as const },
    { title: 'Оплачено', dataIndex: 'orders_paid', key: 'orders_paid', align: 'right' as const },
    { title: 'Завершено', dataIndex: 'orders_completed', key: 'orders_completed', align: 'right' as const },
    {
      title: 'Виручка',
      dataIndex: 'revenue',
      key: 'revenue',
      align: 'right' as const,
      render: (v: number) => formatCurrency(v * 100),
    },
    {
      title: 'Конверсія',
      dataIndex: 'conversion_rate_order',
      key: 'conv',
      align: 'right' as const,
      render: (v: number) => `${v.toFixed(1)}%`,
    },
  ];

  const utmColumns = [
    { title: 'UTM Source', dataIndex: 'utm_source', key: 'utm_source' },
    { title: 'Medium', dataIndex: 'utm_medium', key: 'utm_medium', render: (v: string | null) => v || '—' },
    { title: 'Campaign', dataIndex: 'utm_campaign', key: 'utm_campaign', render: (v: string | null) => v || '—' },
    { title: 'Візити', dataIndex: 'visits', key: 'visits', align: 'right' as const },
    { title: 'Замовлень', dataIndex: 'orders_created', key: 'orders_created', align: 'right' as const },
    { title: 'Оплачено', dataIndex: 'orders_paid', key: 'orders_paid', align: 'right' as const },
    {
      title: 'Виручка',
      dataIndex: 'revenue',
      key: 'revenue',
      align: 'right' as const,
      render: (v: number) => formatCurrency(v * 100),
    },
    {
      title: 'ROAS',
      dataIndex: 'roas',
      key: 'roas',
      align: 'right' as const,
      render: (v: number | null) => v != null ? v.toFixed(2) : '—',
    },
  ];

  return (
    <div>
      <h1>Аналітика</h1>

      {(errorSummary || errorChannel || errorUTM) && (
        <Card style={{ marginBottom: 16, borderColor: '#ff4d4f' }}>
          <span style={{ color: '#ff4d4f' }}>&#x26A0; Помилка завантаження даних. Спробуйте оновити сторінку.</span>
        </Card>
      )}

      <Card style={{ marginBottom: 16 }}>
        <Space>
          <span style={{ fontWeight: 500 }}>Період:</span>
          <RangePicker
            value={[dayjs(start), dayjs(end)]}
            onChange={(dates) => {
              if (dates && dates[0] && dates[1]) {
                setDateRange([dates[0].format('YYYY-MM-DD'), dates[1].format('YYYY-MM-DD')]);
              }
            }}
          />
        </Space>
      </Card>

      {/* KPI Summary */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={8} md={4}>
          <Card>
            <Statistic
              title="Візити"
              value={summary?.total_visits ?? 0}
              loading={loadingSummary}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card>
            <Statistic
              title="Замовлення"
              value={summary?.total_orders ?? 0}
              loading={loadingSummary}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card>
            <Statistic
              title="Оплачено"
              value={summary?.total_payments ?? 0}
              loading={loadingSummary}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card>
            <Statistic
              title="Завершено"
              value={summary?.total_completed ?? 0}
              loading={loadingSummary}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card>
            <Statistic
              title="Виручка"
              value={formatCurrency((summary?.total_revenue ?? 0) * 100)}
              loading={loadingSummary}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card>
            <Statistic
              title="Конверсія"
              value={(summary?.conversion_rate ?? 0).toFixed(1)}
              suffix="%"
              loading={loadingSummary}
            />
          </Card>
        </Col>
      </Row>

      {/* Channel Performance */}
      <Card title="Ефективність за каналами" style={{ marginBottom: 16 }}>
        {channelPerf && channelPerf.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#aaa', padding: 32 }}>
            Немає даних за вибраний період
          </div>
        ) : (
          <Table
            columns={channelColumns}
            dataSource={channelPerf || []}
            loading={loadingChannel}
            rowKey="channel"
            pagination={false}
            size="small"
          />
        )}
      </Card>

      {/* UTM Performance */}
      <Card title="UTM кампанії">
        {utmPerf && utmPerf.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#aaa', padding: 32 }}>
            Немає UTM даних за вибраний період
          </div>
        ) : (
          <Table
            columns={utmColumns}
            dataSource={utmPerf || []}
            loading={loadingUTM}
            rowKey={(r) => `${r.utm_source}-${r.utm_medium}-${r.utm_campaign}`}
            pagination={{ pageSize: 10 }}
            size="small"
            scroll={{ x: 800 }}
          />
        )}
      </Card>
    </div>
  );
};

export default Analytics;
